import React, { useState, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import { css, cx } from '@emotion/css';
import { usePopper } from 'react-popper';
import { theme } from '@contrast-security-inc/design-system-foundations';
import { Text } from '@contrast-security-inc/design-system-typography';
import { useDelayedTooltip } from './useDelayedTooltip';
const arrowHeight = 8;
const arrowWidth = 8;
const arrowStyles = css `
  position: absolute;
  width: ${arrowWidth}px;
  height: ${arrowHeight}px;
  background: inherit;
  visibility: hidden;

  &::after {
    position: absolute;
    width: 8px;
    height: 8px;
    background: inherit;
    visibility: visible;
    content: "";
    transform: rotate(45deg);
    border: 1px solid ${theme.color.neutral90};
    z-index: -1;
  }
`;
const containerStyles = (size) => css `
  border: 1px solid ${theme.color.neutral90};
  background-color: ${theme.color.neutral90};
  border-radius: 4px;
  z-index: ${theme.zIndex.tooltip};
  /* stylelint-disable-next-line declaration-colon-newline-after */
  padding: ${size === 'md' ? `${theme.spacing.px8} ${theme.spacing.px12}` : `${theme.spacing.px4} ${theme.spacing.px8}`};
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
  min-width: 8px;
  max-width: 560px;

  &[data-popper-placement] > span[data-hide] {
    display: none !important;
    visibility: hidden !important;
  }

  &[data-popper-placement^="top"] > span {
    bottom: -3px;
  }

  &[data-popper-placement^="bottom"] > span {
    top: -4px;
  }

  &[data-popper-placement^="left"] > span {
    right: -3px;
  }

  &[data-popper-placement^="right"] > span {
    left: -4px;
  }
`;
const contentStyles = css `
  position: relative;
  z-index: 1;
  background: ${theme.color.neutral90};
  border-radius: 4px;
  width: 100%;
  height: 100%;
`;
const gridStyles = (useInlineTitle) => css `
  display: grid;
  grid-gap: 8px;
  grid-auto-flow: ${useInlineTitle ? 'column' : 'row'};
`;
const renderTooltipContent = ({ element, type, testId, }) => {
    const font = type === 'title' ? 'metadataStrong' : 'metadata';
    const dataTestId = testId ? `${testId}-${type}` : undefined;
    return (React.createElement(Text, { font: font, color: "white", "data-testid": dataTestId }, element));
};
const arrowOffsetMinimum = 8;
export const shouldHideArrow = ({ placement, currentPosition: { x, y, centerOffset }, width, height, }) => {
    if (centerOffset !== 0) {
        return true;
    }
    switch (placement) {
        // if the x position of the arrow is within a threshold of the left edge of the tooltip, hide the arrow
        case 'top-start':
        case 'bottom-start':
            return x ? x < arrowOffsetMinimum : false;
        // if the x position is within a threshold of the right edge of the tooltip, hide the arrow
        case 'top-end':
        case 'bottom-end':
            return x && width ? width - (x + arrowWidth) < arrowOffsetMinimum : false;
        // if the y position is within a threshold of the top of the tooltip, hide the arrow
        case 'left-start':
        case 'right-start':
            return y ? y < arrowOffsetMinimum : false;
        // if the y position is within a threshold of the bootom of the tooltip, hide the arrow
        case 'left-end':
        case 'right-end':
            return y && height ? height - (y + arrowWidth) < arrowOffsetMinimum : false;
        // for all other placements, show the arrow
        default:
            return false;
    }
};
const applyArrowHide = {
    name: 'applyArrowHide',
    enabled: true,
    phase: 'write',
    fn({ state }) {
        const { elements: { arrow }, placement, modifiersData, rects: { popper }, } = state;
        if (arrow) {
            if (shouldHideArrow({ placement, currentPosition: modifiersData.arrow, height: popper.height, width: popper.width })) {
                arrow.setAttribute('data-hide', '');
            }
            else {
                arrow.removeAttribute('data-hide');
            }
        }
    },
};
export const Tooltip = forwardRef(({ enabled = true, children, className, offset = [0, 8], placement = 'top', testId, interactive = false, isDelayed = false, style: userStyles, title, body, hasArrow = true, useInlineTitle = false, openDelay, closeDelay, size = 'md', ...rest }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const [referenceElement, setReferenceElement] = useState(null);
    const { startDelayedOpen, startDelayedClose } = useDelayedTooltip({
        isOpen,
        setIsOpen,
        popperElement,
        arrowElement,
        referenceElement,
        isInteractive: interactive,
        openDelay,
        closeDelay,
    });
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement,
        modifiers: [
            { name: 'arrow', options: { element: arrowElement } },
            { name: 'offset', options: { offset } },
            applyArrowHide,
        ],
    });
    const showTooltip = () => {
        if (isDelayed) {
            startDelayedOpen();
        }
        else {
            setIsOpen(true);
        }
    };
    const hideTooltip = () => {
        if (isDelayed || interactive) {
            startDelayedClose();
        }
        else {
            setIsOpen(false);
        }
    };
    const classes = cx(containerStyles(size), className, 'ds-tooltip');
    const actualPlacement = attributes?.popper?.['data-popper-placement'];
    const setTooltipEvents = (componentChildren) => {
        const events = {};
        const showEvents = ['onMouseOver', 'onFocus'];
        const hideEvents = ['onMouseLeave', 'onBlur'];
        [...showEvents, ...hideEvents].forEach((event) => {
            // eslint-disable-next-line no-prototype-builtins
            if (componentChildren.props.hasOwnProperty(event) && typeof componentChildren.props[event] === 'function') {
                const setTooltipState = showEvents.includes(event) ? showTooltip : hideTooltip;
                events[event] = (evt) => {
                    componentChildren.props[event](evt);
                    setTooltipState();
                };
            }
            else {
                events[event] = showEvents.includes(event) ? showTooltip : hideTooltip;
            }
        });
        return events;
    };
    return (React.createElement(React.Fragment, null,
        React.isValidElement(children) &&
            React.cloneElement(children, {
                // @ts-ignore
                ref: (node) => {
                    setReferenceElement(node);
                    // Call the original ref, if any
                    if (typeof ref === 'function') {
                        ref(node);
                    }
                    else if (ref !== null) {
                        ref.current = node;
                    }
                },
                ...setTooltipEvents(children),
            }),
        enabled &&
            isOpen &&
            ReactDOM.createPortal(React.createElement("div", { onMouseOver: showTooltip, onMouseLeave: hideTooltip, onFocus: showTooltip, onBlur: hideTooltip, "data-popper-placement": placement, className: classes, ref: setPopperElement, style: {
                    ...styles.popper,
                    ...userStyles,
                }, "data-testid": testId, ...attributes.popper, ...rest },
                React.createElement("div", { className: contentStyles },
                    React.createElement("div", { className: gridStyles(useInlineTitle), "data-testid": testId && `${testId}-content` },
                        title && renderTooltipContent({ element: title, type: 'title', testId }),
                        body && renderTooltipContent({ element: body, type: 'body', testId }))),
                actualPlacement && hasArrow && (React.createElement("span", { "data-popper-placement": actualPlacement, className: arrowStyles, ref: setArrowElement, "data-testid": testId && `${testId}-arrow`, style: styles.arrow }))), document.body)));
});
Tooltip.displayName = 'Tooltip';
